(() => {
  const MODAL_ID = 'bf-esim-compat-modal';
  const BODY_ID = 'bf-esim-compat-modal-body';

  // ------------------------------------------------------------
  // Helpers to build consistent result messages
  // ------------------------------------------------------------
  const MANUAL_CHECK_MESSAGE_PHONE = [
    'Go to Settings → Connections (or Mobile Network) → SIM manager / Mobile network.',
    'If you see an option like “Add eSIM”, “Add mobile plan”, or “Download SIM”, your device supports eSIM.',
    'Carrier lock, region, and firmware may affect final compatibility.'
  ];

  const MANUAL_CHECK_MESSAGE_WATCH = [
    'Check whether your smartwatch is the LTE / Cellular version (Bluetooth-only models usually do not support eSIM).',
    'Open the companion app on your phone and look for a mobile plan / eSIM setup option.',
    'Carrier support for smartwatch eSIM varies by country.'
  ];

  const MANUAL_CHECK_MESSAGE_TABLET = [
    'Check whether your tablet is a Cellular / 4G / 5G model (Wi-Fi only models do not support eSIM).',
    'Go to Settings → Mobile Data / Cellular / Network and look for “Add eSIM” or “Add mobile plan”.',
    'Region and carrier support may vary.'
  ];

  const MANUAL_CHECK_MESSAGE_LAPTOP = [
    'Your laptop must have a WWAN / cellular modem to support eSIM.',
    'On Windows, go to Settings → Network & Internet → Cellular. If you see “Manage eSIM profiles” / “Add profile”, it supports eSIM.',
    'Many laptops are sold in both cellular and non-cellular variants.'
  ];

  function resultYes(extraNotes = []) {
    return {
      mode: 'compatible_yes',
      title: 'Your device appears to support eSIM ✅',
      compatible: true,
      notes: [
        'Your device may still need to be carrier-unlocked.',
        'Final compatibility can depend on region, carrier, and firmware.',
        ...extraNotes
      ]
    };
  }

  function resultNo(extraNotes = []) {
    return {
      mode: 'compatible_no',
      title: 'Your device may not support eSIM',
      compatible: false,
      notes: [
        'Please check the exact model name and regional version to confirm.',
        ...extraNotes
      ]
    };
  }

  function resultUnknownManual(deviceType = 'phone', extraNotes = []) {
    const base =
      deviceType === 'watch'
        ? MANUAL_CHECK_MESSAGE_WATCH
        : deviceType === 'tablet'
        ? MANUAL_CHECK_MESSAGE_TABLET
        : deviceType === 'laptop'
        ? MANUAL_CHECK_MESSAGE_LAPTOP
        : MANUAL_CHECK_MESSAGE_PHONE;

    return {
      mode: 'manual_check',
      title: 'Check your device compatibility',
      compatible: null,
      notes: [...base, ...extraNotes]
    };
  }

  // ------------------------------------------------------------
  // Database
  // ------------------------------------------------------------
  // IMPORTANT:
  // - eSIM support can vary by region/carrier/firmware.
  // - For uncertain cases use esim: null to show manual-check message.
  // - Grouping "series" is intentionally broad for UX simplicity.
  // ------------------------------------------------------------
  const DB = {
    root: {
      title: 'Select your device*',
      subtitle: '',
      options: [
        { id: 'phone', label: 'Phone' },
        { id: 'smartwatch', label: 'Smartwatch' },
        { id: 'tablet', label: 'Tablet' },
        { id: 'laptop', label: 'Laptop' }
      ]
    },

    flows: {
      // ---------------- PHONE BRANDS ----------------
      phone: {
        title: 'Select your phone brand*',
        subtitle: 'To find your model: Settings → About phone → Device name.',
        options: [
          { id: 'apple_phone', label: 'Apple (iPhone)' },
          { id: 'samsung_phone', label: 'Samsung Galaxy' },
          { id: 'google_phone', label: 'Google Pixel' },
          { id: 'xiaomi_phone', label: 'Xiaomi / Redmi / POCO' },
          { id: 'huawei_phone', label: 'Huawei' },
          { id: 'honor_phone', label: 'Honor' },
          { id: 'oppo_phone', label: 'OPPO' },
          { id: 'oneplus_phone', label: 'OnePlus' },
          { id: 'vivo_phone', label: 'vivo' },
          { id: 'motorola_phone', label: 'Motorola' },
          { id: 'sony_phone', label: 'Sony Xperia' },
          { id: 'nokia_phone', label: 'Nokia / HMD' },
          { id: 'asus_phone', label: 'ASUS' },
          { id: 'nothing_phone', label: 'Nothing' },
          { id: 'fairphone_phone', label: 'Fairphone' },
          { id: 'realme_phone', label: 'realme' },
          { id: 'other_phone', label: 'Other phone brand' }
        ]
      },

      // ---------------- SMARTWATCH BRANDS ----------------
      smartwatch: {
        title: 'Select your smartwatch brand*',
        subtitle: 'Only LTE / Cellular models usually support eSIM.',
        options: [
          { id: 'apple_watch_brand', label: 'Apple Watch' },
          { id: 'samsung_watch_brand', label: 'Samsung Galaxy Watch' },
          { id: 'google_watch_brand', label: 'Google Pixel Watch' },
          { id: 'huawei_watch_brand', label: 'Huawei Watch' },
          { id: 'xiaomi_watch_brand', label: 'Xiaomi Watch' },
          { id: 'oppo_watch_brand', label: 'OPPO Watch' },
          { id: 'garmin_watch_brand', label: 'Garmin (selected LTE models)' },
          { id: 'ticwatch_brand', label: 'TicWatch' },
          { id: 'other_watch', label: 'Other smartwatch brand' }
        ]
      },

      // ---------------- TABLET BRANDS ----------------
      tablet: {
        title: 'Select your tablet brand*',
        subtitle: 'Wi-Fi only tablets do not support eSIM.',
        options: [
          { id: 'ipad_tablet', label: 'Apple iPad' },
          { id: 'samsung_tablet', label: 'Samsung Galaxy Tab' },
          { id: 'microsoft_tablet', label: 'Microsoft Surface (tablet/2-in-1)' },
          { id: 'lenovo_tablet', label: 'Lenovo Tablet / Tab' },
          { id: 'huawei_tablet', label: 'Huawei MatePad' },
          { id: 'xiaomi_tablet', label: 'Xiaomi Pad' },
          { id: 'other_tablet', label: 'Other tablet brand' }
        ]
      },

      // ---------------- LAPTOP BRANDS ----------------
      laptop: {
        title: 'Select your laptop brand*',
        subtitle: 'Your laptop must have a WWAN / cellular modem to support eSIM.',
        options: [
          { id: 'windows_laptop_brand', label: 'Windows laptop (Dell / HP / Lenovo / etc.)' },
          { id: 'surface_laptop_brand', label: 'Microsoft Surface Laptop / Pro' },
          { id: 'chromebook_brand', label: 'Chromebook' },
          { id: 'macbook_brand', label: 'Apple MacBook' },
          { id: 'other_laptop', label: 'Other laptop brand' }
        ]
      }
    },

    models: {
      // ============================================================
      // PHONES
      // ============================================================
      apple_phone: {
        title: 'Select your iPhone model*',
        subtitle: '',
        options: [
          { id: 'iphone_16_series', label: 'iPhone 16 / 16 Plus / 16 Pro / 16 Pro Max', esim: true },
          { id: 'iphone_15_series', label: 'iPhone 15 / 15 Plus / 15 Pro / 15 Pro Max', esim: true },
          { id: 'iphone_14_series', label: 'iPhone 14 / 14 Plus / 14 Pro / 14 Pro Max', esim: true },
          { id: 'iphone_13_series', label: 'iPhone 13 / 13 mini / 13 Pro / 13 Pro Max', esim: true },
          { id: 'iphone_12_series', label: 'iPhone 12 / 12 mini / 12 Pro / 12 Pro Max', esim: true },
          { id: 'iphone_11_series', label: 'iPhone 11 / 11 Pro / 11 Pro Max', esim: true },
          { id: 'iphone_xs_xr_series', label: 'iPhone XS / XS Max / XR', esim: true },
          { id: 'iphone_se_3', label: 'iPhone SE (3rd generation)', esim: true },
          { id: 'iphone_se_2', label: 'iPhone SE (2nd generation)', esim: true },
          { id: 'iphone_x_or_older', label: 'iPhone X / 8 / 7 / 6 / older', esim: false },
          { id: 'iphone_other_unknown', label: 'Other / I am not sure', esim: null }
        ],
        notes: [
          'Most iPhones from XS / XR and newer support eSIM.',
          'Support can vary by region and carrier restrictions.'
        ],
        deviceType: 'phone'
      },

      samsung_phone: {
        title: 'Select your Samsung Galaxy phone*',
        subtitle: '',
        options: [
          { id: 'galaxy_s24_series', label: 'Galaxy S24 / S24+ / S24 Ultra', esim: true },
          { id: 'galaxy_s23_series', label: 'Galaxy S23 / S23+ / S23 Ultra / S23 FE', esim: true },
          { id: 'galaxy_s22_series', label: 'Galaxy S22 / S22+ / S22 Ultra', esim: true },
          { id: 'galaxy_s21_series', label: 'Galaxy S21 / S21+ / S21 Ultra / S21 FE', esim: true },
          { id: 'galaxy_s20_series', label: 'Galaxy S20 / S20+ / S20 Ultra (selected regions)', esim: null },
          { id: 'galaxy_note20_series', label: 'Galaxy Note20 / Note20 Ultra', esim: true },
          { id: 'galaxy_z_fold6_fold5_fold4', label: 'Galaxy Z Fold6 / Fold5 / Fold4', esim: true },
          { id: 'galaxy_z_fold3_or_older', label: 'Galaxy Z Fold3 / Fold2 / Fold (region-dependent)', esim: null },
          { id: 'galaxy_z_flip6_flip5_flip4', label: 'Galaxy Z Flip6 / Flip5 / Flip4', esim: true },
          { id: 'galaxy_z_flip3_or_older', label: 'Galaxy Z Flip3 / older (region-dependent)', esim: null },
          { id: 'galaxy_a_series', label: 'Galaxy A series (many models vary by region)', esim: null },
          { id: 'galaxy_m_f_series', label: 'Galaxy M / F series', esim: null },
          { id: 'samsung_other_unknown', label: 'Other Samsung / I am not sure', esim: null }
        ],
        notes: [
          'Samsung eSIM support depends heavily on exact model code, region, firmware, and carrier.',
          'Some devices support eSIM in one country but not in another.'
        ],
        deviceType: 'phone'
      },

      google_phone: {
        title: 'Select your Google Pixel model*',
        subtitle: '',
        options: [
          { id: 'pixel_9_series', label: 'Pixel 9 / 9 Pro / 9 Pro XL / 9 Pro Fold', esim: true },
          { id: 'pixel_8_series', label: 'Pixel 8 / 8 Pro / 8a', esim: true },
          { id: 'pixel_7_series', label: 'Pixel 7 / 7 Pro / 7a', esim: true },
          { id: 'pixel_6_series', label: 'Pixel 6 / 6 Pro / 6a', esim: true },
          { id: 'pixel_5_and_4_series', label: 'Pixel 5 / 5a / 4 / 4 XL / 4a / 4a 5G', esim: true },
          { id: 'pixel_3_series', label: 'Pixel 3 / 3 XL / 3a / 3a XL', esim: true },
          { id: 'pixel_2_series', label: 'Pixel 2 / 2 XL', esim: true },
          { id: 'pixel_1', label: 'Original Pixel (1st gen)', esim: null },
          { id: 'pixel_other_unknown', label: 'Other Pixel / I am not sure', esim: null }
        ],
        notes: [
          'Most Pixel models from Pixel 2 and newer support eSIM.',
          'Carrier and country restrictions may apply.'
        ],
        deviceType: 'phone'
      },

      xiaomi_phone: {
        title: 'Select your Xiaomi / Redmi / POCO phone*',
        subtitle: '',
        options: [
          { id: 'xiaomi_14_series', label: 'Xiaomi 14 series (selected models/regions)', esim: null },
          { id: 'xiaomi_13_series', label: 'Xiaomi 13 series (selected models/regions)', esim: null },
          { id: 'xiaomi_12t_pro_or_flagships', label: 'Selected flagship Xiaomi models (region-dependent)', esim: null },
          { id: 'xiaomi_mix_fold_series', label: 'Xiaomi foldable models (selected)', esim: null },
          { id: 'redmi_series', label: 'Redmi series (usually no eSIM / varies)', esim: null },
          { id: 'poco_series', label: 'POCO series (usually no eSIM / varies)', esim: null },
          { id: 'xiaomi_other_unknown', label: 'Other Xiaomi / Redmi / POCO / I am not sure', esim: null }
        ],
        notes: [
          'Xiaomi ecosystem eSIM support varies significantly by exact model and region.',
          'Always check the device settings for “Add eSIM / Add mobile plan”.'
        ],
        deviceType: 'phone'
      },

      huawei_phone: {
        title: 'Select your Huawei phone*',
        subtitle: '',
        options: [
          { id: 'huawei_p40_series', label: 'Huawei P40 / P40 Pro / P40 Pro+ (selected variants)', esim: null },
          { id: 'huawei_mate40_series', label: 'Huawei Mate40 series (selected variants)', esim: null },
          { id: 'huawei_mate_x_series', label: 'Huawei Mate X / foldable series (selected)', esim: null },
          { id: 'huawei_p_mate_older', label: 'Older Huawei P / Mate models', esim: null },
          { id: 'huawei_other_unknown', label: 'Other Huawei / I am not sure', esim: null }
        ],
        notes: [
          'Huawei eSIM support is highly model- and region-specific.',
          'Please check your exact variant and settings.'
        ],
        deviceType: 'phone'
      },

      honor_phone: {
        title: 'Select your Honor phone*',
        subtitle: '',
        options: [
          { id: 'honor_magic_series', label: 'Honor Magic series (selected models)', esim: null },
          { id: 'honor_fold_series', label: 'Honor foldable series (selected models)', esim: null },
          { id: 'honor_number_x_series', label: 'Honor Number / X series', esim: null },
          { id: 'honor_other_unknown', label: 'Other Honor / I am not sure', esim: null }
        ],
        notes: ['Honor eSIM support varies by model and region.'],
        deviceType: 'phone'
      },

      oppo_phone: {
        title: 'Select your OPPO phone*',
        subtitle: '',
        options: [
          { id: 'oppo_find_x_series', label: 'OPPO Find X series (selected models)', esim: null },
          { id: 'oppo_find_n_foldables', label: 'OPPO Find N foldables (selected models)', esim: null },
          { id: 'oppo_reno_series', label: 'OPPO Reno series (selected models)', esim: null },
          { id: 'oppo_a_series', label: 'OPPO A series', esim: null },
          { id: 'oppo_other_unknown', label: 'Other OPPO / I am not sure', esim: null }
        ],
        notes: ['OPPO eSIM support depends on exact model/region/carrier.'],
        deviceType: 'phone'
      },

      oneplus_phone: {
        title: 'Select your OnePlus phone*',
        subtitle: '',
        options: [
          { id: 'oneplus_12_11_series', label: 'OnePlus 12 / 12R / 11 series (selected regions)', esim: null },
          { id: 'oneplus_open', label: 'OnePlus Open (selected regions)', esim: null },
          { id: 'oneplus_10_9_series', label: 'OnePlus 10 / 9 series', esim: null },
          { id: 'oneplus_nord_series', label: 'OnePlus Nord series', esim: null },
          { id: 'oneplus_other_unknown', label: 'Other OnePlus / I am not sure', esim: null }
        ],
        notes: ['OnePlus eSIM support varies by region and firmware.'],
        deviceType: 'phone'
      },

      vivo_phone: {
        title: 'Select your vivo phone*',
        subtitle: '',
        options: [
          { id: 'vivo_x_series', label: 'vivo X series (selected models)', esim: null },
          { id: 'vivo_fold_series', label: 'vivo foldables (selected models)', esim: null },
          { id: 'vivo_v_y_series', label: 'vivo V / Y series', esim: null },
          { id: 'vivo_other_unknown', label: 'Other vivo / I am not sure', esim: null }
        ],
        notes: ['vivo eSIM support is limited and region-dependent.'],
        deviceType: 'phone'
      },

      motorola_phone: {
        title: 'Select your Motorola phone*',
        subtitle: '',
        options: [
          { id: 'motorola_razr_2023_2024', label: 'Motorola razr / razr+ (recent models)', esim: true },
          { id: 'motorola_edge_series', label: 'Motorola Edge series (selected models)', esim: null },
          { id: 'motorola_g_series', label: 'Moto G series', esim: null },
          { id: 'motorola_other_unknown', label: 'Other Motorola / I am not sure', esim: null }
        ],
        notes: ['Motorola eSIM support varies by specific model and market.'],
        deviceType: 'phone'
      },

      sony_phone: {
        title: 'Select your Sony Xperia phone*',
        subtitle: '',
        options: [
          { id: 'xperia_1_series', label: 'Xperia 1 series (selected models/regions)', esim: null },
          { id: 'xperia_5_series', label: 'Xperia 5 series (selected models/regions)', esim: null },
          { id: 'xperia_10_series', label: 'Xperia 10 series (selected models/regions)', esim: null },
          { id: 'xperia_other_unknown', label: 'Other Xperia / I am not sure', esim: null }
        ],
        notes: ['Sony Xperia eSIM support varies by exact model code and region.'],
        deviceType: 'phone'
      },

      nokia_phone: {
        title: 'Select your Nokia / HMD phone*',
        subtitle: '',
        options: [
          { id: 'nokia_xr21_x30_selected', label: 'Selected Nokia/HMD models (e.g. some XR/X series)', esim: null },
          { id: 'nokia_feature_phone', label: 'Nokia feature phone / classic phone', esim: false },
          { id: 'nokia_other_unknown', label: 'Other Nokia / HMD / I am not sure', esim: null }
        ],
        notes: ['HMD/Nokia support varies by exact model and region.'],
        deviceType: 'phone'
      },

      asus_phone: {
        title: 'Select your ASUS phone*',
        subtitle: '',
        options: [
          { id: 'asus_zenfone_selected', label: 'Zenfone (selected models)', esim: null },
          { id: 'asus_rog_phone_series', label: 'ROG Phone series', esim: null },
          { id: 'asus_other_unknown', label: 'Other ASUS / I am not sure', esim: null }
        ],
        notes: ['ASUS phone eSIM support is model-dependent and may be limited.'],
        deviceType: 'phone'
      },

      nothing_phone: {
        title: 'Select your Nothing phone*',
        subtitle: '',
        options: [
          { id: 'nothing_phone_2', label: 'Nothing Phone (2)', esim: null },
          { id: 'nothing_phone_2a', label: 'Nothing Phone (2a) / (2a) Plus', esim: null },
          { id: 'nothing_phone_1', label: 'Nothing Phone (1)', esim: null },
          { id: 'nothing_other_unknown', label: 'Other Nothing / I am not sure', esim: null }
        ],
        notes: ['Nothing eSIM support may vary by model and market.'],
        deviceType: 'phone'
      },

      fairphone_phone: {
        title: 'Select your Fairphone*',
        subtitle: '',
        options: [
          { id: 'fairphone_5', label: 'Fairphone 5', esim: null },
          { id: 'fairphone_4', label: 'Fairphone 4', esim: null },
          { id: 'fairphone_other_unknown', label: 'Other Fairphone / I am not sure', esim: null }
        ],
        notes: ['Fairphone eSIM support can vary by software version and market.'],
        deviceType: 'phone'
      },

      realme_phone: {
        title: 'Select your realme phone*',
        subtitle: '',
        options: [
          { id: 'realme_gt_series', label: 'realme GT series (selected models)', esim: null },
          { id: 'realme_number_series', label: 'realme Number series', esim: null },
          { id: 'realme_other_unknown', label: 'Other realme / I am not sure', esim: null }
        ],
        notes: ['realme eSIM support is limited and varies by model/region.'],
        deviceType: 'phone'
      },

      other_phone: {
        direct: resultUnknownManual('phone', [
          'Brand not listed: your phone may still support eSIM.',
          'Check the exact model and region variant in Settings → About phone.'
        ])
      },

      // ============================================================
      // SMARTWATCHES
      // ============================================================
      apple_watch_brand: {
        title: 'Select your Apple Watch*',
        subtitle: '',
        options: [
          { id: 'apple_watch_ultra', label: 'Apple Watch Ultra / Ultra 2', esim: true },
          { id: 'apple_watch_series_cellular', label: 'Apple Watch Series (GPS + Cellular)', esim: true },
          { id: 'apple_watch_se_cellular', label: 'Apple Watch SE (GPS + Cellular)', esim: true },
          { id: 'apple_watch_gps_only', label: 'Apple Watch GPS (non-cellular)', esim: false },
          { id: 'apple_watch_unknown', label: 'I am not sure if it is Cellular', esim: null }
        ],
        notes: ['Only GPS + Cellular versions support eSIM.'],
        deviceType: 'watch'
      },

      samsung_watch_brand: {
        title: 'Select your Samsung Galaxy Watch*',
        subtitle: '',
        options: [
          { id: 'galaxy_watch7_lte', label: 'Galaxy Watch7 / Ultra (LTE)', esim: true },
          { id: 'galaxy_watch6_lte', label: 'Galaxy Watch6 / Classic (LTE)', esim: true },
          { id: 'galaxy_watch5_lte', label: 'Galaxy Watch5 / Pro (LTE)', esim: true },
          { id: 'galaxy_watch4_lte', label: 'Galaxy Watch4 / Classic (LTE)', esim: true },
          { id: 'galaxy_watch_lte_older', label: 'Older Galaxy Watch LTE models', esim: true },
          { id: 'galaxy_watch_bluetooth_only', label: 'Bluetooth-only Galaxy Watch', esim: false },
          { id: 'galaxy_watch_unknown', label: 'I am not sure if it is LTE', esim: null }
        ],
        notes: ['Only LTE variants support eSIM.'],
        deviceType: 'watch'
      },

      google_watch_brand: {
        title: 'Select your Google Pixel Watch*',
        subtitle: '',
        options: [
          { id: 'pixel_watch_3_lte', label: 'Pixel Watch 3 (LTE)', esim: true },
          { id: 'pixel_watch_2_lte', label: 'Pixel Watch 2 (LTE)', esim: true },
          { id: 'pixel_watch_1_lte', label: 'Pixel Watch (LTE)', esim: true },
          { id: 'pixel_watch_wifi', label: 'Wi-Fi / Bluetooth-only Pixel Watch', esim: false },
          { id: 'pixel_watch_unknown', label: 'I am not sure if it is LTE', esim: null }
        ],
        notes: ['Only LTE versions support eSIM.'],
        deviceType: 'watch'
      },

      huawei_watch_brand: {
        title: 'Select your Huawei Watch*',
        subtitle: '',
        options: [
          { id: 'huawei_watch_lte_selected', label: 'Huawei Watch LTE / eSIM models (selected)', esim: true },
          { id: 'huawei_watch_non_lte', label: 'Bluetooth / non-LTE Huawei Watch', esim: false },
          { id: 'huawei_watch_unknown', label: 'I am not sure', esim: null }
        ],
        notes: ['Huawei smartwatch eSIM support depends on model and country/carrier support.'],
        deviceType: 'watch'
      },

      xiaomi_watch_brand: {
        title: 'Select your Xiaomi Watch*',
        subtitle: '',
        options: [
          { id: 'xiaomi_watch_esim_selected', label: 'Xiaomi Watch eSIM/LTE models (selected)', esim: true },
          { id: 'xiaomi_watch_bluetooth_only', label: 'Bluetooth-only Xiaomi Watch', esim: false },
          { id: 'xiaomi_watch_unknown', label: 'I am not sure', esim: null }
        ],
        notes: ['Xiaomi smartwatch eSIM support is model-specific and may be region-limited.'],
        deviceType: 'watch'
      },

      oppo_watch_brand: {
        title: 'Select your OPPO Watch*',
        subtitle: '',
        options: [
          { id: 'oppo_watch_esim_selected', label: 'OPPO Watch eSIM/LTE models (selected)', esim: true },
          { id: 'oppo_watch_non_lte', label: 'Bluetooth / non-LTE OPPO Watch', esim: false },
          { id: 'oppo_watch_unknown', label: 'I am not sure', esim: null }
        ],
        notes: ['OPPO smartwatch eSIM support varies by model and market.'],
        deviceType: 'watch'
      },

      garmin_watch_brand: {
        title: 'Select your Garmin smartwatch*',
        subtitle: '',
        options: [
          { id: 'garmin_lte_selected', label: 'Garmin LTE model (selected models only)', esim: true },
          { id: 'garmin_non_lte', label: 'Standard Garmin watch (no LTE)', esim: false },
          { id: 'garmin_unknown', label: 'I am not sure', esim: null }
        ],
        notes: ['Only selected Garmin LTE models support mobile connectivity/eSIM.'],
        deviceType: 'watch'
      },

      ticwatch_brand: {
        title: 'Select your TicWatch*',
        subtitle: '',
        options: [
          { id: 'ticwatch_pro_3_lte', label: 'TicWatch Pro 3 LTE (selected markets)', esim: true },
          { id: 'ticwatch_non_lte', label: 'Non-LTE TicWatch models', esim: false },
          { id: 'ticwatch_unknown', label: 'I am not sure', esim: null }
        ],
        notes: ['Support depends on model and local carrier availability.'],
        deviceType: 'watch'
      },

      other_watch: {
        direct: resultUnknownManual('watch', [
          'Your smartwatch may support eSIM if it is an LTE/Cellular version.'
        ])
      },

      // ============================================================
      // TABLETS
      // ============================================================
      ipad_tablet: {
        title: 'Select your iPad*',
        subtitle: '',
        options: [
          { id: 'ipad_pro_cellular', label: 'iPad Pro (Cellular)', esim: true },
          { id: 'ipad_air_cellular', label: 'iPad Air (Cellular)', esim: true },
          { id: 'ipad_mini_cellular', label: 'iPad mini (Cellular)', esim: true },
          { id: 'ipad_standard_cellular', label: 'iPad (standard) Cellular models', esim: true },
          { id: 'ipad_wifi_only', label: 'Any iPad Wi-Fi only model', esim: false },
          { id: 'ipad_unknown', label: 'I am not sure if it is Cellular', esim: null }
        ],
        notes: ['Only Cellular iPad models support eSIM.'],
        deviceType: 'tablet'
      },

      samsung_tablet: {
        title: 'Select your Samsung Galaxy Tab*',
        subtitle: '',
        options: [
          { id: 'galaxy_tab_s_lte_5g', label: 'Galaxy Tab S series LTE/5G model', esim: null },
          { id: 'galaxy_tab_a_lite_lte_5g', label: 'Galaxy Tab A / Active / Lite LTE/5G model', esim: null },
          { id: 'galaxy_tab_wifi_only', label: 'Wi-Fi only Galaxy Tab', esim: false },
          { id: 'galaxy_tab_unknown', label: 'I am not sure if it is LTE/5G', esim: null }
        ],
        notes: ['Samsung tablet eSIM support varies by exact model and region.'],
        deviceType: 'tablet'
      },

      microsoft_tablet: {
        title: 'Select your Microsoft Surface device*',
        subtitle: '',
        options: [
          { id: 'surface_pro_x', label: 'Surface Pro X (selected configs)', esim: true },
          { id: 'surface_pro_9_5g', label: 'Surface Pro 9 with 5G (selected configs)', esim: true },
          { id: 'surface_pro_lte_selected', label: 'Surface Pro LTE / 5G models (selected)', esim: true },
          { id: 'surface_wifi_only', label: 'Surface Wi-Fi only / no cellular model', esim: false },
          { id: 'surface_unknown', label: 'I am not sure if it has cellular', esim: null }
        ],
        notes: ['Surface support depends on whether the device includes cellular hardware.'],
        deviceType: 'tablet'
      },

      lenovo_tablet: {
        title: 'Select your Lenovo tablet*',
        subtitle: '',
        options: [
          { id: 'lenovo_tab_cellular_selected', label: 'Lenovo tablet with LTE/5G (selected)', esim: null },
          { id: 'lenovo_tab_wifi', label: 'Wi-Fi only Lenovo tablet', esim: false },
          { id: 'lenovo_tab_unknown', label: 'I am not sure', esim: null }
        ],
        notes: ['Lenovo tablet eSIM support is model-dependent and not common on all LTE models.'],
        deviceType: 'tablet'
      },

      huawei_tablet: {
        title: 'Select your Huawei MatePad*',
        subtitle: '',
        options: [
          { id: 'matepad_cellular_selected', label: 'MatePad Cellular/LTE model (selected)', esim: null },
          { id: 'matepad_wifi_only', label: 'Wi-Fi only MatePad', esim: false },
          { id: 'matepad_unknown', label: 'I am not sure', esim: null }
        ],
        notes: ['Huawei tablet eSIM support depends on exact model/market.'],
        deviceType: 'tablet'
      },

      xiaomi_tablet: {
        title: 'Select your Xiaomi Pad*',
        subtitle: '',
        options: [
          { id: 'xiaomi_pad_cellular_selected', label: 'Xiaomi Pad cellular model (selected)', esim: null },
          { id: 'xiaomi_pad_wifi_only', label: 'Wi-Fi only Xiaomi Pad', esim: false },
          { id: 'xiaomi_pad_unknown', label: 'I am not sure', esim: null }
        ],
        notes: ['Xiaomi tablet eSIM support is limited and model-specific.'],
        deviceType: 'tablet'
      },

      other_tablet: {
        direct: resultUnknownManual('tablet', [
          'Many tablets are sold as Wi-Fi only (no eSIM support).'
        ])
      },

      // ============================================================
      // LAPTOPS
      // ============================================================
      windows_laptop_brand: {
        title: 'Select your Windows laptop type*',
        subtitle: '',
        options: [
          { id: 'windows_laptop_business_wwan', label: 'Business laptop with WWAN / LTE / 5G module', esim: true },
          { id: 'windows_laptop_consumer_no_cellular', label: 'Standard consumer laptop (no cellular)', esim: false },
          { id: 'windows_laptop_unknown', label: 'I am not sure if it has WWAN/cellular', esim: null }
        ],
        notes: [
          'Popular WWAN-capable lines include selected Dell Latitude, HP EliteBook/ZBook, Lenovo ThinkPad models.',
          'The same laptop model name may have both cellular and non-cellular variants.'
        ],
        deviceType: 'laptop'
      },

      surface_laptop_brand: {
        title: 'Select your Microsoft Surface laptop/2-in-1*',
        subtitle: '',
        options: [
          { id: 'surface_pro_with_cellular', label: 'Surface Pro with LTE/5G (selected models)', esim: true },
          { id: 'surface_laptop_with_cellular_selected', label: 'Surface device with cellular hardware (selected)', esim: true },
          { id: 'surface_no_cellular', label: 'Surface device without cellular hardware', esim: false },
          { id: 'surface_laptop_unknown', label: 'I am not sure', esim: null }
        ],
        notes: ['Check Windows Cellular settings to confirm eSIM profile support.'],
        deviceType: 'laptop'
      },

      chromebook_brand: {
        title: 'Select your Chromebook*',
        subtitle: '',
        options: [
          { id: 'chromebook_lte_esim_selected', label: 'Chromebook with LTE/eSIM (selected enterprise models)', esim: null },
          { id: 'chromebook_standard', label: 'Standard Chromebook (Wi-Fi only)', esim: false },
          { id: 'chromebook_unknown', label: 'I am not sure', esim: null }
        ],
        notes: ['Chromebook eSIM support is uncommon and device-specific.'],
        deviceType: 'laptop'
      },

      macbook_brand: {
        title: 'Select your MacBook*',
        subtitle: '',
        options: [
          { id: 'macbook_any', label: 'Any MacBook (Air / Pro)', esim: false }
        ],
        notes: [
          'MacBook models do not include built-in cellular eSIM for mobile plans.',
          'You can still use a phone hotspot or USB modem.'
        ],
        deviceType: 'laptop'
      },

      other_laptop: {
        direct: resultUnknownManual('laptop', [
          'Many laptops do not have cellular hardware even if they look identical to WWAN-capable versions.'
        ])
      }
    }
  };

  // ------------------------------------------------------------
  // Runtime state
  // ------------------------------------------------------------
  let modal, body, selected = null, stack = [];

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  function esc(v) {
    return String(v ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function lockScroll(on) {
    document.documentElement.classList.toggle('bf-modal-open', on);
    document.body.classList.toggle('bf-modal-open', on);
  }

  function refreshRefs() {
    modal = document.getElementById(MODAL_ID);
    body = document.getElementById(BODY_ID);
    return !!(modal && body);
  }

  function letters(i) {
    return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[i] || String(i + 1);
  }

function renderOptions(options) {
  return `
    <div class="bf-esim-grid">
      ${options.map((o, i) => `
        <button type="button" class="bf-esim-option" data-action="pick" data-value="${esc(o.id)}" aria-pressed="false">
          <span class="bf-esim-option__badge notranslate" translate="no">${letters(i)}</span>
          <span class="bf-esim-option__label notranslate" translate="no">${esc(o.label)}</span>
        </button>
      `).join('')}
    </div>
  `;
}

  function renderStep({ title, subtitle = '', options = [], back = false }) {
    body.innerHTML = `
      <div class="bf-esim-step bf-esim-step--wide">
        <h2 class="bf-esim-title" id="bf-esim-compat-modal-title">${esc(title)}</h2>
        ${subtitle ? `<p class="bf-esim-subtitle">${esc(subtitle)}</p>` : ''}
        ${renderOptions(options)}
        <div class="bf-esim-actions">
          ${back ? `<button type="button" class="bf-esim-btn bf-esim-btn--ghost" data-action="back">Back</button>` : ''}
          <button type="button" class="bf-esim-btn bf-esim-btn--primary" data-action="next">Continue</button>
        </div>
      </div>
    `;
    selected = null;
  }

  function renderResult({ title, compatible = null, notes = [], mode = '' }) {
    const cls =
      compatible === true
        ? 'bf-esim-result__ok'
        : compatible === false
        ? 'bf-esim-result__bad'
        : 'bf-esim-result__unknown';

    const isManual = mode === 'manual_check';

    body.innerHTML = `
      <div class="bf-esim-step bf-esim-step--wide">
        <h2 class="${cls}" id="bf-esim-compat-modal-title">${esc(title)}</h2>
        ${notes.map(n => `<p class="bf-esim-note">${esc(n)}</p>`).join('')}
        <div class="bf-esim-actions">
          <button type="button" class="bf-esim-btn bf-esim-btn--ghost" data-action="${isManual ? 'backToList' : 'restart'}">
            ${isManual ? 'Back to list' : 'Start again'}
          </button>
          <button type="button" class="bf-esim-btn bf-esim-btn--primary" data-action="close">Close</button>
        </div>
      </div>
    `;
    selected = null;
  }

  function buildResultFromChoice(group, picked) {
    // Option-level notes can override/add context
    const groupNotes = Array.isArray(group.notes) ? group.notes : [];
    const optionNotes = Array.isArray(picked.notes) ? picked.notes : [];
    const combined = [...groupNotes, ...optionNotes];

    if (picked.esim === true) return resultYes(combined);
    if (picked.esim === false) return resultNo(combined);

    // unknown / null -> manual check message based on group device type
    return resultUnknownManual(group.deviceType || 'phone', combined);
  }

  function openModal() {
    if (!refreshRefs()) return;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    lockScroll(true);
    stack = [{ type: 'root' }];
    renderStep(DB.root);
  }

  function closeModal() {
    if (!refreshRefs()) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    lockScroll(false);
    selected = null;
    stack = [];
    body.innerHTML = '';
  }

  function current() {
    return stack[stack.length - 1];
  }

  function rerenderCurrent() {
    const c = current();
    if (!c) return;
    if (c.type === 'root') return renderStep(DB.root);
    if (c.type === 'flow') return renderStep({ ...DB.flows[c.key], back: true });
    if (c.type === 'models') return renderStep({ ...DB.models[c.key], back: true });
  }

  function nextStep() {
    const c = current();
    if (!c || !selected) return;

    // Root -> Flow
    if (c.type === 'root') {
      const flow = DB.flows[selected];
      if (!flow) return;
      stack.push({ type: 'flow', key: selected });
      return rerenderCurrent();
    }

    // Flow -> Models or direct result
    if (c.type === 'flow') {
      const model = DB.models[selected];
      if (!model) return;
      if (model.direct) {
        stack.push({ type: 'result' });
        return renderResult(model.direct);
      }
      stack.push({ type: 'models', key: selected });
      return rerenderCurrent();
    }

    // Models -> Result
    if (c.type === 'models') {
      const group = DB.models[c.key];
      if (!group || !Array.isArray(group.options)) return;

      const picked = group.options.find(o => o.id === selected);
      if (!picked) return;

      stack.push({ type: 'result' });
      return renderResult(buildResultFromChoice(group, picked));
    }
  }

  function goBack() {
    if (stack.length <= 1) {
      stack = [{ type: 'root' }];
      return rerenderCurrent();
    }

    stack.pop();

    while (current() && current().type === 'result') {
      stack.pop();
    }

    if (!stack.length) stack = [{ type: 'root' }];
    rerenderCurrent();
  }

  function onDocClick(e) {
    const openBtn = e.target.closest('[data-esim-modal-open]');
    if (openBtn) {
      e.preventDefault();
      openModal();
      return;
    }

    if (!modal || modal.hidden) return;

    // Close button / backdrop / custom close target
    if (e.target.closest('[data-esim-close]')) {
      e.preventDefault();
      closeModal();
      return;
    }

    const pick = e.target.closest('[data-action="pick"]');
    if (pick) {
      e.preventDefault();

      $$('.bf-esim-option', body).forEach(btn => {
        btn.classList.remove('is-selected');
        btn.setAttribute('aria-pressed', 'false');
      });

      pick.classList.add('is-selected');
      pick.setAttribute('aria-pressed', 'true');
      selected = pick.dataset.value;
      return;
    }

    if (e.target.closest('[data-action="next"]')) {
      e.preventDefault();
      nextStep();
      return;
    }

    if (e.target.closest('[data-action="back"]')) {
      e.preventDefault();
      goBack();
      return;
    }

    if (e.target.closest('[data-action="restart"]')) {
      e.preventDefault();
      stack = [{ type: 'root' }];
      rerenderCurrent();
      return;
    }

    if (e.target.closest('[data-action="backToList"]')) {
      e.preventDefault();
      stack = [{ type: 'root' }];
      rerenderCurrent();
      return;
    }

    if (e.target.closest('[data-action="close"]')) {
      e.preventDefault();
      closeModal();
      return;
    }
  }

  function onKey(e) {
    if (!modal || modal.hidden) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal();
    }
  }

  function init() {
    refreshRefs();
    // Use capture phase to avoid other scripts blocking the click
    document.addEventListener('click', onDocClick, true);
    document.addEventListener('keydown', onKey);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.ButterflyESIMCompatModal = {
    open: openModal,
    close: closeModal
  };
})();